import { useCallback, useEffect, useState } from 'react';
import {
  Stack, Box, Text, Modal, Center, Tooltip, LoadingOverlay, Button, Anchor, ScrollArea, Divider
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useNavigate, useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';

import { IconFileSpark } from '@tabler/icons-react';

import type {
  Proposal, RequirementResponse, ProposalSection, WithoutPermissions, RouteWithGet
} from '../../../types/apiTypes';
import { editorContentIsEmpty } from '../../../utils/stringUtils';
import { defaultModalProps } from '../../../utils/mantineUtils';
import { EditRequirement, GenerateAllWarning } from '../../../components/Modals';
import { type NarrativeOverlayState } from './EditorPane';
import OutlineResponseEditor from '../ResponseEditors/OutlineResponseEditor';
import { type ViewSelectorProps } from '../../../components';
import useEnsisMutation from '../../../hooks/useEnsisMutation';
import {
  type RequirementType,
  DEFAULT_EMPTY_REQUIREMENT
} from '../../../utils/requirementUtils';
import { withAnalyticsEvent } from '../../../utils/analyticsUtils';
import { OutlineOutputFormatType } from '../../../utils/constants';
import { resetUpdatedTexts } from '../../../redux/OutlineEditorSlice';
import { selectOutlineIsDirty } from '../../../redux/store';
import OutlineEditorRequirement from '../../../components/OutlineEditorRequirement/OutlineEditorRequirement';
import EditorPaneContainer from './EditorPaneContainer';

type Props = {
  section: WithoutPermissions<ProposalSection>
  proposal: Proposal
  requirementResponses: RequirementResponse[]
  canEdit: boolean
  narrativeOverlayState?: NarrativeOverlayState
  complianceCheckIsRunning: boolean
  focusedRequirementUid: string
  onSelectRequirement?: (reqUid: string) => void
} & ViewSelectorProps;

const ALERT_COPY = 'Import relevant past work and information about your organization into your Resources, ' +
  'and try again.';

const OutlineEditor: React.FC<Props> = (props: Props) => {
  const {
    section,
    proposal,
    requirementResponses,
    canEdit,
    focusedRequirementUid,
    onSelectRequirement
  } = props;
  const { proposalUid, sectionUid, organization } = useParams();
  const [failedResponseUids, setFailedResponseUids] = useState<string[]>([]);
  const [currentRequirement, setCurrentRequirement] = useState<RequirementType>(DEFAULT_EMPTY_REQUIREMENT);
  const [editRequirementOpened, editRequirementHandlers] = useDisclosure();
  const [generateAllWarningOpened, genereateAllWarningHandlers] = useDisclosure();
  const outlineIsDirty = useSelector(selectOutlineIsDirty);

  const sectionIsEmpty = (requirementResponses?.length ?? 0) === 0;
  const [shouldShowWarning, setShouldShowWarning] = useState(section.content !== null);
  const navigate = useNavigate();
  const outlineOutputFormat = proposal.outline_output_format ?? OutlineOutputFormatType.BULLET_POINTS;
  /* eslint-disable-next-line max-len */
  const requirementResponseQueryRoute: RouteWithGet = `/app/proposals/${proposalUid}/sections/${sectionUid}/requirement-responses`;
  const editRequirementComplianceMutation = useEnsisMutation('/app/requirement-responses', {
    requestType: 'patch',
    queryKeysToInvalidate: [requirementResponseQueryRoute],
    showSuccessMessage: false
  });
  const dispatch = useDispatch();
  const generateRequirementResponses = useEnsisMutation(
    '/app/proposal-editing/generate-responses',
    {
      contentType: 'application/json',
      requestType: 'post',
      showSuccessMessage: false,
      queryKeysToInvalidate: [requirementResponseQueryRoute],
      onSuccess: (data) => {
        const failedUids = data.responses.filter((response) => (
          response.content === ''
        )).map((response) => response.uid);
        setFailedResponseUids(failedUids);
        const updatedUids = data.responses.map((response) => response.uid);
        dispatch(resetUpdatedTexts(updatedUids));
      }
    }
  );

  useEffect(() => {
    setShouldShowWarning(section.content !== null);
  }, [section]);

  const onEditRequirement = useCallback((requirementResponseUid: string) => {
    // mutatation clears compliance for edited requirement
    editRequirementComplianceMutation.mutate({
      proposal_uid: proposalUid ?? '',
      edits: [{
        requirement_response_uid: requirementResponseUid,
        section_text_references: [],
        is_compliant: false
      }]
    });
  }, [editRequirementComplianceMutation, proposalUid]);

  const handleGoToProposalManagement = useCallback(() => {
    navigate(`/org/${organization}/proposals/${proposalUid}/manage`);
  }, [organization, proposalUid]);

  const outlineEmptyState = (
    <Center
      mt={8}
      style={{
        borderRadius: 4
      }}
      h={250}
      ta='center'
    >
      <Text p={8}>
        {'This section has no requirements. Add requirements here, go to '}
        <Anchor onClick={handleGoToProposalManagement}>Proposal Management</Anchor>
        {' to find and place unsorted requirements, or click \'Create Narrative\' \
        to generate content without requirements.'}
      </Text>
    </Center>
  );

  const emptyResponses = requirementResponses.filter((response) => (
    editorContentIsEmpty(response.content)
  ));
  const hasEmptyResponses = emptyResponses.length > 0;

  const onClickGenerateAllResponses = useCallback(() => {
    generateRequirementResponses.mutate({
      requirement_response_uids: emptyResponses.map((response) => response.uid)
    });
  }, [generateRequirementResponses, emptyResponses]);

  const onClickGenerateSingleResponse = useCallback((requirementResponse: RequirementResponse) => {
    generateRequirementResponses.mutate({
      requirement_response_uids: [requirementResponse.uid]
    });
  }, [generateRequirementResponses]);

  const resetFailedUids = useCallback(() => { setFailedResponseUids([]); }, []);
  const handleClickGenerateAll = useCallback(() => {
    if (outlineIsDirty) {
      genereateAllWarningHandlers.open();
    } else {
      withAnalyticsEvent(onClickGenerateAllResponses, 'GenerateAllResponses')();
    }
  }, [outlineIsDirty, onClickGenerateAllResponses]);
  const getRequirementAndResponseEditor = useCallback((response: RequirementResponse) => (
    <Box
      key={response.uid}
      flex={1}
    >
      <OutlineEditorRequirement
        canEdit={canEdit}
        response={response}
        proposal={proposal}
        onSelectRequirement={onSelectRequirement}
        onOpenEditRequirement={editRequirementHandlers.open}
        setCurrentRequirement={setCurrentRequirement}
        isSelected={response.requirement?.uid === focusedRequirementUid}
      />
      <OutlineResponseEditor
        canEdit={canEdit}
        responseData={response}
        shouldShowWarning={shouldShowWarning}
        setShouldShowWarningFalse={() => { setShouldShowWarning(false); }}
        outlineOutputFormat={outlineOutputFormat}
        onGenerateText={() => { onClickGenerateSingleResponse(response); }}
        alertText={failedResponseUids.includes(response.uid) ? ALERT_COPY : undefined}
        onEditorUpdate={resetFailedUids}
      />
      <Divider mx={props.isFullScreen ? 0 : 12} mt={16} />
    </Box>
  ), [
    canEdit,
    proposal,
    onSelectRequirement,
    focusedRequirementUid,
    resetFailedUids,
    failedResponseUids,
    onClickGenerateSingleResponse,
    outlineOutputFormat,
    shouldShowWarning
  ]);

  const outlineContentEditors = (
    <Stack gap={16} pt={16}>
      {requirementResponses.map((response) => {
        return getRequirementAndResponseEditor(response);
      })}
    </Stack>
  );

  const generateAllButton = (
    <Tooltip
      events={{
        hover: true,
        focus: false,
        touch: false
      }}
      label={hasEmptyResponses ? 'Generates all missing responses' : 'No responses to generate'}
    >
      <Button
        variant='subtle'
        onClick={handleClickGenerateAll}
        disabled={!hasEmptyResponses}
        leftSection={<IconFileSpark size={20} />}
        styles={{
          section: { marginRight: 8 }
        }}
      >
        Generate Empty
      </Button>
    </Tooltip>
  );

  return (
    <>
      <Modal opened={generateAllWarningOpened} {...defaultModalProps}>
        <GenerateAllWarning
          onClose={genereateAllWarningHandlers.close}
          onGenerateAll={withAnalyticsEvent(onClickGenerateAllResponses, 'GenerateAllResponses')}
        />
      </Modal>
      <Modal opened={editRequirementOpened} {...defaultModalProps} zIndex={1000}>
        <EditRequirement
          close={editRequirementHandlers.close}
          requirement={currentRequirement}
          onEditRequirement={() => { onEditRequirement(currentRequirement.requirementResponseUid); }}
        />
      </Modal>
      <Stack gap={0} flex={1}>
        <LoadingOverlay visible={generateRequirementResponses.isPending} />
        <EditorPaneContainer {...props} rightSection={generateAllButton}>
          <ScrollArea.Autosize
            flex={1}
            type='hover'
          >
            {sectionIsEmpty ? outlineEmptyState : outlineContentEditors}
          </ScrollArea.Autosize>
        </EditorPaneContainer>
      </Stack>
    </>
  );
};

export default OutlineEditor;
